import { Component, DestroyRef, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdminService, Activity, Costcenter, Customer, Employee, Project, Unit } from '../../../core/services/admin.service';
import { AdminFeedbackService } from '../../../core/services/admin-feedback.service';
import { ModalService } from '../../../core/services/modal.service';
import {
  ActivityNewPriceDialogResult,
  ActivityNewPriceModalComponent,
} from '../../../shared/components/modal/activity-new-price-modal.component';
import { formatCostcenterLabel } from '../../../shared/utils/costcenter.utils';
import { EMPLOYEE_ACTIVE_FILTER_OPTIONS } from '../../../shared/constants/ui-options';

type ActivityFilter = 'all' | 'active' | 'inactive';
const INTERNAL_CUSTOMER_ID = 100;

interface PriceUpdateRow {
  activity: Activity;
  customer: Customer | null;
  project: Project | null;
  employee: Employee | null;
  unit: Unit | null;
  costcenter: Costcenter | null;
}

@Component({
  selector: 'app-admin-activity-price-tab',
  standalone: false,
  template: require('./admin-activity-price-tab.component.html').default,
  styles: [require('!!raw-loader!sass-loader!./admin-activity-price-tab.component.scss').default],
})
export class AdminActivityPriceTabComponent implements OnChanges {
  @Input() activities: Activity[] = [];
  @Input() projects: Project[] = [];
  @Input() customers: Customer[] = [];
  @Input() employees: Employee[] = [];
  @Input() units: Unit[] = [];
  @Input() costcenters: Costcenter[] = [];
  @Output() dataUpdated = new EventEmitter<void>();

  activeFilter: ActivityFilter = 'active';
  activeFilterOptions = EMPLOYEE_ACTIVE_FILTER_OPTIONS;

  selectedCustomer: Customer | null = null;
  selectedProject: Project | null = null;
  selectedActivity: Activity | null = null;

  filteredCustomers: Customer[] = [];
  filteredProjects: Project[] = [];
  filteredActivityOptions: Activity[] = [];
  rows: PriceUpdateRow[] = [];

  private destroyRef = inject(DestroyRef);

  constructor(
    @Inject(AdminService) private adminService: AdminService,
    @Inject(AdminFeedbackService) private adminFeedback: AdminFeedbackService,
    @Inject(ModalService) private modalService: ModalService
  ) {}

  ngOnChanges(_changes: SimpleChanges): void {
    this.rebuildFiltersAndRows();
  }

  onCustomerChange(): void {
    this.selectedProject = null;
    this.selectedActivity = null;
    this.rebuildFiltersAndRows();
  }

  onProjectChange(): void {
    this.selectedActivity = null;
    this.rebuildFiltersAndRows();
  }

  onActivityChange(): void {
    this.rebuildRowsOnly();
  }

  onActiveFilterChange(filter: ActivityFilter): void {
    this.activeFilter = filter;
    this.selectedActivity = null;
    this.rebuildFiltersAndRows();
  }

  getProjectOptionLabel(project: Project | null | undefined): string {
    if (!project) {
      return '';
    }
    return project.endcustomer ? `${project.project_name} (${project.endcustomer})` : project.project_name;
  }

  getActivityOptionLabel(activity: Activity | null | undefined): string {
    if (!activity) {
      return '';
    }
    const employeeName = this.employees.find((employee) => employee.employee_id === activity.employee_id)?.name || '';
    const start = activity.start_date || '';
    const stop = activity.stop_date || '';
    return `${activity.activity_name}, ${employeeName} (${start} - ${stop})`;
  }

  async openNewPriceModal(row: PriceUpdateRow): Promise<void> {
    const result = (await this.modalService.show(ActivityNewPriceModalComponent, {
      activity: row.activity,
      projectLabel: this.getProjectOptionLabel(row.project),
      employeeLabel: row.employee?.name ?? 'Okänd',
      unitLabel: row.unit?.description_s ?? 'Okänd',
      costcenterLabel: row.costcenter ? formatCostcenterLabel(row.costcenter) : 'Inget',
    })) as ActivityNewPriceDialogResult | undefined;

    if (!result) {
      return;
    }

    this.adminService
      .newPriceActivity(row.activity.activity_id, result)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.dataUpdated.emit();
        },
        error: (err) => this.adminFeedback.showCrudError('uppdatera', 'aktivitet', err),
      });
  }

  private rebuildFiltersAndRows(): void {
    const currentCustomerId = this.selectedCustomer?.customer_id ?? null;
    const currentProjectId = this.selectedProject?.project_id ?? null;
    const currentActivityId = this.selectedActivity?.activity_id ?? null;

    this.filteredCustomers = this.customers
      .filter((customer) => this.isExternalCustomer(customer.customer_id))
      .sort((a, b) => a.customer_id - b.customer_id);

    this.selectedCustomer =
      this.filteredCustomers.find((customer) => customer.customer_id === currentCustomerId) || this.selectedCustomer;

    if (currentCustomerId !== null && !this.selectedCustomer) {
      this.selectedCustomer = null;
    }

    this.filteredProjects = this.projects
      .filter((project) => this.isExternalCustomer(project.customer_id))
      .filter((project) => this.selectedCustomer == null || project.customer_id === this.selectedCustomer.customer_id)
      .sort((a, b) => a.project_id - b.project_id);

    this.selectedProject =
      this.filteredProjects.find((project) => project.project_id === currentProjectId) || this.selectedProject;

    if (currentProjectId !== null && !this.selectedProject) {
      this.selectedProject = null;
    }

    this.filteredActivityOptions = this.getBaseFilteredActivities().sort((a, b) => {
      const projectCompare = a.project_id - b.project_id;
      if (projectCompare !== 0) return projectCompare;
      return (a.start_date || '').localeCompare(b.start_date || '');
    });

    this.selectedActivity =
      this.filteredActivityOptions.find((activity) => activity.activity_id === currentActivityId) || this.selectedActivity;

    if (currentActivityId !== null && !this.selectedActivity) {
      this.selectedActivity = null;
    }

    this.rebuildRowsOnly();
  }

  private rebuildRowsOnly(): void {
    this.rows = this.getFilteredActivitiesForTable()
      .map((activity) => this.buildRow(activity))
      .sort((a, b) => {
        const customerCompare = (a.customer?.customername || '').localeCompare(b.customer?.customername || '', 'sv');
        if (customerCompare !== 0) return customerCompare;
        const projectCompare = (a.project?.project_name || '').localeCompare(b.project?.project_name || '', 'sv');
        if (projectCompare !== 0) return projectCompare;
        const employeeCompare = (a.employee?.name || '').localeCompare(b.employee?.name || '', 'sv');
        if (employeeCompare !== 0) return employeeCompare;
        const activityCompare = a.activity.activity_name.localeCompare(b.activity.activity_name, 'sv');
        if (activityCompare !== 0) return activityCompare;
        return (a.activity.start_date || '').localeCompare(b.activity.start_date || '');
      });
  }

  private getBaseFilteredActivities(): Activity[] {
    return this.activities.filter((activity) => {
      if (this.activeFilter === 'active' && activity.stop_date !== null) {
        return false;
      }
      if (this.activeFilter === 'inactive' && activity.stop_date === null) {
        return false;
      }
      if (!this.isExternalActivity(activity.project_id)) {
        return false;
      }
      if (this.selectedCustomer && !this.projectMatchesCustomer(activity.project_id, this.selectedCustomer.customer_id)) {
        return false;
      }
      if (this.selectedProject && activity.project_id !== this.selectedProject.project_id) {
        return false;
      }
      return true;
    });
  }

  private getFilteredActivitiesForTable(): Activity[] {
    const base = this.getBaseFilteredActivities();
    if (this.selectedActivity) {
      return base.filter((activity) => activity.activity_id === this.selectedActivity?.activity_id);
    }
    return base;
  }

  private projectMatchesCustomer(projectId: number, customerId: number): boolean {
    return this.projects.some((project) => project.project_id === projectId && project.customer_id === customerId);
  }

  private buildRow(activity: Activity): PriceUpdateRow {
    const project = this.projects.find((candidate) => candidate.project_id === activity.project_id) || null;
    const customer = project
      ? this.customers.find((candidate) => candidate.customer_id === project.customer_id) || null
      : null;
    const employee = this.employees.find((candidate) => candidate.employee_id === activity.employee_id) || null;
    const unit = this.units.find((candidate) => candidate.unit_id === activity.unit_id) || null;
    const costcenter =
      this.costcenters.find((candidate) => String(candidate.external_id) === activity.costcenter) || null;

    return {
      activity,
      project,
      customer,
      employee,
      unit,
      costcenter,
    };
  }

  private isExternalActivity(projectId: number): boolean {
    const project = this.projects.find((candidate) => candidate.project_id === projectId);
    return project ? this.isExternalCustomer(project.customer_id) : false;
  }

  private isExternalCustomer(customerId: number): boolean {
    return customerId > INTERNAL_CUSTOMER_ID;
  }
}
